<template>
  <div ref="wrap" class="dc">
    <div class="dc-plot" tabindex="0" role="group" :aria-label="ariaLabel" @keydown="onKey" @focus="onFocus" @blur="active = -1"
         @pointerleave="active = -1">
      <svg :width="width" :height="height" :viewBox="'0 0 ' + width + ' ' + height" aria-hidden="true" focusable="false">
        <g class="dc-grid">
          <line v-for="tick in ticks" :key="'g' + tick" :x1="left" :x2="width - right" :y1="y(tick)" :y2="y(tick)" :class="{ base: tick === 0 }" />
        </g>
        <g class="dc-y">
          <text v-for="tick in ticks" :key="'y' + tick" :x="left - 8" :y="y(tick) + 4" text-anchor="end">{{ compact(tick) }}</text>
        </g>
        <g class="dc-x">
          <text v-for="index in xTicks" :key="'x' + index" :x="center(index)" :y="height - 8" text-anchor="middle">{{ short(series[index].day) }}</text>
        </g>
        <g class="dc-bars">
          <path v-for="(point, index) in series" v-show="point.value > 0" :key="point.day" :d="bar(index, point.value)"
                :class="{ on: index === active }" />
        </g>
        <text v-if="peak >= 0 && series[peak].value > 0 && active < 0" class="dc-peak" :x="center(peak)" :y="y(series[peak].value) - 7" text-anchor="middle">{{ series[peak].value }}</text>
        <g class="dc-hits">
          <rect v-for="(point, index) in series" :key="'h' + point.day" :x="left + index * slot" :y="top" :width="slot" :height="plotHeight"
                @pointerenter="active = index" @pointermove="active = index" />
        </g>
      </svg>
      <div v-if="active >= 0" class="dc-tip" :style="tipStyle" role="presentation">
        <strong>{{ series[active].value.toLocaleString('en-GB') }}</strong>
        <span>{{ long(series[active].day) }}</span>
      </div>
    </div>
    <p class="pb-sr-only" aria-live="polite">{{ announcement }}</p>
  </div>
</template>

<script>
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function niceStep(max, count) {
  const raw = Math.max(1, max) / count;
  const power = Math.pow(10, Math.floor(Math.log10(raw)));
  const unit = [1, 2, 5, 10].find(step => step * power >= raw) * power;
  return Math.max(1, unit);
}

export default {
  name: 'DailyChart',
  props: {
    // [{day: 'YYYY-MM-DD', value: number}]
    series: { type: Array, default: () => [] },
    label: { type: String, default: 'Count' },
    height: { type: Number, default: 240 }
  },
  data: () => ({ width: 640, active: -1, observer: null, left: 40, right: 8, top: 24, bottom: 30 }),
  computed: {
    plotWidth() { return Math.max(40, this.width - this.left - this.right); },
    plotHeight() { return this.height - this.top - this.bottom; },
    max() { return this.series.reduce((m, point) => Math.max(m, point.value), 0); },
    step() { return niceStep(this.max, 4); },
    yMax() { return Math.max(this.step * Math.ceil(this.max / this.step), this.step * 2); },
    ticks() { const list = []; for (let t = 0; t <= this.yMax; t += this.step) list.push(t); return list; },
    slot() { return this.plotWidth / Math.max(1, this.series.length); },
    barWidth() { return Math.max(1, Math.min(24, this.slot - 2)); },
    peak() {
      let best = -1;
      this.series.forEach((point, index) => { if (best < 0 || point.value > this.series[best].value) best = index; });
      return best;
    },
    xTicks() {
      const n = this.series.length;
      if (!n) return [];
      const every = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(this.plotWidth / 78))));
      const list = [];
      for (let i = 0; i < n; i += every) list.push(i);
      if (n - 1 - list[list.length - 1] >= every / 2) list.push(n - 1);
      return list;
    },
    tipStyle() {
      const x = this.center(this.active);
      const left = Math.min(Math.max(x, 70), this.width - 70);
      return { left: left + 'px', top: Math.max(0, this.y(this.series[this.active].value) - 58) + 'px' };
    },
    ariaLabel() {
      if (!this.series.length) return this.label + ': no data';
      return this.label + ' per day, ' + this.long(this.series[0].day) + ' to ' + this.long(this.series[this.series.length - 1].day)
        + '. Highest ' + this.max + '. Use the left and right arrow keys to read each day.';
    },
    announcement() {
      return this.active >= 0 ? this.long(this.series[this.active].day) + ': ' + this.series[this.active].value + ' ' + this.label.toLowerCase() : '';
    }
  },
  mounted() {
    this.measure();
    if (window.ResizeObserver) {
      this.observer = new ResizeObserver(() => this.measure());
      this.observer.observe(this.$refs.wrap);
    } else {
      window.addEventListener('resize', this.measure);
    }
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('resize', this.measure);
  },
  methods: {
    measure() { if (this.$refs.wrap) this.width = Math.max(240, Math.floor(this.$refs.wrap.clientWidth)); },
    y(value) { return this.top + this.plotHeight - (value / this.yMax) * this.plotHeight; },
    center(index) { return this.left + index * this.slot + this.slot / 2; },
    // A column with a 4px rounded data end and a square foot on the baseline.
    bar(index, value) {
      const w = this.barWidth;
      const x0 = this.center(index) - w / 2;
      const x1 = x0 + w;
      const base = this.y(0);
      const top = this.y(value);
      const r = Math.min(4, w / 2, base - top);
      return 'M' + x0 + ' ' + base + 'V' + (top + r) + 'Q' + x0 + ' ' + top + ' ' + (x0 + r) + ' ' + top + 'H' + (x1 - r)
        + 'Q' + x1 + ' ' + top + ' ' + x1 + ' ' + (top + r) + 'V' + base + 'Z';
    },
    compact(value) { return value >= 1000 ? (Math.round(value / 100) / 10) + 'K' : String(value); },
    parts(day) { const [y, m, d] = String(day).split('-').map(Number); return { y, m, d }; },
    short(day) { const p = this.parts(day); return p.d + ' ' + MONTHS[p.m - 1]; },
    long(day) { const p = this.parts(day); return p.d + ' ' + MONTHS[p.m - 1] + ' ' + p.y; },
    onFocus() { if (this.active < 0 && this.series.length) this.active = this.series.length - 1; },
    onKey(event) {
      const n = this.series.length;
      if (!n) return;
      const moves = { ArrowLeft: -1, ArrowRight: 1 };
      if (moves[event.key]) { this.active = Math.min(n - 1, Math.max(0, (this.active < 0 ? n - 1 : this.active) + moves[event.key])); event.preventDefault(); }
      if (event.key === 'Home') { this.active = 0; event.preventDefault(); }
      if (event.key === 'End') { this.active = n - 1; event.preventDefault(); }
    }
  }
};
</script>

<style scoped>
.dc{position:relative;width:100%}
.dc-plot{position:relative;outline:none;border-radius:10px}
.dc-plot:focus-visible{outline:2px solid #B4472B;outline-offset:4px}
.dc svg{display:block;overflow:visible}
.dc-grid line{stroke:#EFE9E0;stroke-width:1;shape-rendering:crispEdges}
.dc-grid line.base{stroke:#D4C9B8}
.dc-y text,.dc-x text{font:500 11.5px var(--font-body);fill:#6B635B;font-variant-numeric:tabular-nums}
.dc-bars path{fill:#B4472B;transition:fill .12s ease}
.dc-bars path.on{fill:#7C2F1B}
.dc-peak{font:600 12px var(--font-body);fill:#4F4A45}
.dc-hits rect{fill:transparent;cursor:crosshair}
.dc-tip{position:absolute;transform:translateX(-50%);padding:7px 11px;border-radius:9px;background:#1F2328;pointer-events:none;
  display:flex;flex-direction:column;align-items:center;white-space:nowrap;box-shadow:0 6px 18px rgba(31,35,40,.22)}
.dc-tip strong{color:#fff;font:700 15px/1.2 var(--font-body)}
.dc-tip span{color:#D9D2C8;font:500 12px/1.3 var(--font-body)}
@media(prefers-reduced-motion:reduce){.dc-bars path{transition:none}}
</style>
